import { AiLoadingPanel, Css, pageContentPaddingX, useModal } from "@homebound/beam";
import { useEffect } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { AiProposalStatus, FeatureFlagType, PlanPackageAiProposalWorkflow } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { AiProposalFailureBanner } from "src/routes/libraries/plan-package-v2/components/AiProposalFailureBanner";
import { AiProposalFailureBanner_AiProposalFragment } from "src/routes/libraries/plan-package-v2/components/AiProposalFailureBanner.generated";
import { GenerateTakeoffModal } from "src/routes/libraries/plan-package-v2/components/takeoffs/GenerateTakeoffModal";
import { TakeoffsSidePanelProvider } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsSidePanelProvider";
import { TakeoffsAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsAiReviewBanner";
import { TakeoffsTabHeader } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsTabHeader";
import { TakeoffsTable } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsTable";
import { useTakeoffsTabAiProposalQuery } from "src/routes/libraries/plan-package-v2/components/TakeoffsTab.generated";
import type { PlanPackageTabProps } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2";
import {
  TakeoffsManagerProvider,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";

export function TakeoffsTab(props: PlanPackageTabProps) {
  const { planPackageId, versionId } = props;
  const isTakeoffAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesTakeoffAi);
  const { data, startPolling, stopPolling } = useTakeoffsTabAiProposalQuery({
    variables: { id: planPackageId, versionId },
    skip: !isTakeoffAiEnabled,
  });
  const proposal = data?.planPackage.aiProposal;
  const isGeneration = proposal?.workflow?.code === PlanPackageAiProposalWorkflow.GenerateTakeoff;
  const isProcessing = isGeneration && proposal?.status.code === AiProposalStatus.Pending;
  const hasGenerationFailed = isGeneration && proposal?.status.code === AiProposalStatus.Failed;
  useEffect(() => {
    isProcessing ? startPolling(10_000) : stopPolling();
  }, [isProcessing, startPolling, stopPolling]);

  return (
    <TakeoffsManagerProvider planPackageId={planPackageId} versionId={versionId} syncViewToUrl={false}>
      <TakeoffsSidePanelProvider>
        {/* The banner's query doesn't poll, so mounting it once processing ends is what fetches the new proposals */}
        {!isProcessing && <TakeoffsAiReviewBanner />}
        <TakeoffsTabHeader />
        {hasGenerationFailed && <TakeoffsAiFailureBanner proposal={proposal} />}
        {isProcessing ? (
          // The header has no bottom padding, so this keeps its actions from sitting flush on the panel
          <div css={Css.pt3.$}>
            <AiLoadingPanel
              title="We're processing your document..."
              message="If you leave this page the processing will continue and your results will show up here for your review and acceptance. You can overwrite any imported content."
              progressText={proposal.progressText ?? undefined}
            />
          </div>
        ) : (
          <TakeoffsTable />
        )}
      </TakeoffsSidePanelProvider>
    </TakeoffsManagerProvider>
  );
}

type TakeoffsAiFailureBannerProps = { proposal: AiProposalFailureBanner_AiProposalFragment };

function TakeoffsAiFailureBanner(props: TakeoffsAiFailureBannerProps) {
  const { proposal } = props;
  const canEditLineItems = useTakeoffsStore((state) => state.planPackage.canEditLineItems);
  const { openModal } = useModal();
  return (
    <div css={{ ...Css.layoutContainer.pt3.$, ...pageContentPaddingX }}>
      <AiProposalFailureBanner
        proposal={proposal}
        tryAgainDisabled={disableBasedOnPotentialOperation(canEditLineItems)}
        onTryAgain={() =>
          openModal({ size: "lg", aiMode: true, drawHeaderBorder: true, content: <GenerateTakeoffModal /> })
        }
      />
    </div>
  );
}
